<template>
  <v-container fluid class="mt-16 px-6">
    <div class="pl pr">
      <v-card flat>
        <v-card-title class="d-flex">
          <h2>Quotations</h2>
          <v-spacer></v-spacer>
          <add-quotation-dialog />
        </v-card-title>
        <v-card-text>
          <!-- Quotation Mode Filter -->
          <div class="d-flex flex-wrap align-center mb-3">
            <v-radio-group
              v-model="modeFilter"
              row
              dense
              hide-details
              class="mt-0 mr-6"
              label="Filter by Mode:"
            >
              <v-radio label="All" value="all"></v-radio>
              <v-radio label="Walk-in" value="walk_in"></v-radio>
              <v-radio label="Government" value="government"></v-radio>
            </v-radio-group>

            <v-radio-group
              v-model="statusFilter"
              row
              dense
              hide-details
              class="mt-0"
              label="Filter by Status:"
            >
              <v-radio label="All" value="all"></v-radio>
              <v-radio label="Drafts" value="draft"></v-radio>
              <v-radio label="Finalized" value="finalized"></v-radio>
            </v-radio-group>
          </div>

          <!-- Customer Name and Company Name Filters -->
          <v-row dense class="mb-2">
            <v-col cols="12" md="4">
              <v-text-field
                prepend-icon="mdi-account-search"
                label="Filter by Customer Name"
                v-model="customerNameFilter"
                clearable
                dense
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                prepend-icon="mdi-domain"
                label="Filter by Company Name"
                v-model="companyNameFilter"
                clearable
                dense
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                prepend-icon="mdi-magnify"
                label="General Search"
                v-model="search"
                clearable
                dense
                hide-details
              ></v-text-field>
            </v-col>
          </v-row>

          <v-data-table
            :search="search"
            :items="filteredQuotations"
            :headers="headers"
            :items-per-page="15"
          >
            <template v-slot:item.is_draft="{ item }">
              <v-chip v-if="item.is_draft" color="amber darken-1" dark x-small>
                <v-icon left x-small>mdi-pencil-outline</v-icon>
                Draft
              </v-chip>
              <v-chip v-else color="green" dark x-small> Finalized </v-chip>
              <v-chip
                v-if="item.converted_invoice_id"
                color="teal"
                dark
                x-small
                class="ml-1"
              >
                Invoiced
              </v-chip>
            </template>
            <template v-slot:item.quotation_mode="{ item }">
              <v-chip
                :color="
                  (item.quotation_mode || 'walk_in') === 'government'
                    ? 'blue'
                    : 'green'
                "
                dark
                x-small
              >
                {{
                  (item.quotation_mode || "walk_in") === "government"
                    ? "Gov"
                    : "Walk-in"
                }}
              </v-chip>
            </template>
            <template v-slot:item.total_quote_price="{ item }">
              {{ formatCurrency(item.total_quote_price || item.total) }}
            </template>
            <template v-slot:item.updated_at="{ item }">
              {{ formatDate(item.updated_at) }}
            </template>
            <template v-slot:item.actions="{ item }">
              <div class="d-flex">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      :color="item.is_pinned ? 'amber darken-2' : 'grey'"
                      @click="togglePin(item)"
                      :loading="pinLoading === item.id"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon>{{
                        item.is_pinned ? "mdi-pin" : "mdi-pin-outline"
                      }}</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ item.is_pinned ? "Unpin" : "Pin to top" }}</span>
                </v-tooltip>
                <view-quotation-modal :item="item" />
                <add-quotation-dialog :edit-item="item" />
                <v-btn icon color="green" @click="openPrintDialog(item)">
                  <v-icon>mdi-printer</v-icon>
                </v-btn>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      color="blue"
                      @click="duplicateQuotation(item)"
                      :loading="duplicateLoading === item.id"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon>mdi-content-copy</v-icon>
                    </v-btn>
                  </template>
                  <span>Duplicate</span>
                </v-tooltip>
                <v-btn icon color="error" @click="del(item)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
                <convert-to-invoice-launcher
                  :quotation="item"
                  @convert="onConvertQuote"
                />
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </div>

    <!-- Print Options Dialog -->
    <v-dialog v-model="printDialog" max-width="440">
      <v-card rounded="lg">
        <v-card-title class="subtitle-1 font-weight-bold pb-1">
          <v-icon color="green" class="mr-2">mdi-printer</v-icon>
          Export Quotation
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="body-2 grey--text text--darken-2 mb-4">
            Configure export options for Quote
            <strong>#{{ printTarget ? printTarget.quote_number : "" }}</strong>
          </div>

          <v-divider class="mb-4"></v-divider>

          <!-- Export Mode -->
          <div class="body-2 font-weight-medium mb-2">Export as</div>
          <v-radio-group
            v-model="exportMode"
            row
            dense
            hide-details
            class="mt-0 mb-4"
          >
            <v-radio label="Print / PDF" value="pdf" color="green"></v-radio>
            <v-radio label="Copy as Image" value="image" color="teal"></v-radio>
          </v-radio-group>

          <v-divider class="mb-4"></v-divider>

          <v-checkbox
            v-model="printOptions.hideAllItems"
            dense
            hide-details
            color="teal"
            class="mt-0 mb-2"
          >
            <template v-slot:label>
              <div>
                <span class="body-2 font-weight-medium">
                  Hide item prices (show items only)
                </span>
                <br />
                <span class="caption grey--text">
                  Items are still listed, but the Unit Price / Amount columns
                  are hidden. Only the total is shown.
                </span>
              </div>
            </template>
          </v-checkbox>

          <v-checkbox
            v-model="printOptions.hideBundleItemPrices"
            dense
            hide-details
            color="teal"
            class="mt-0"
            :disabled="printOptions.hideAllItems"
          >
            <template v-slot:label>
              <div>
                <span class="body-2 font-weight-medium">
                  Hide individual bundle item prices
                </span>
                <br />
                <span class="caption grey--text">
                  Only the bundle subtotal will be shown. Standalone items are
                  not affected.
                </span>
              </div>
            </template>
          </v-checkbox>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn
            text
            class="text-none"
            @click="printDialog = false"
            :disabled="copyingImage"
          >
            Cancel
          </v-btn>
          <v-btn
            color="green"
            depressed
            dark
            class="text-none px-4"
            :loading="copyingImage"
            @click="confirmPrint"
          >
            <v-icon left small>{{
              exportMode === "image" ? "mdi-content-copy" : "mdi-printer"
            }}</v-icon>
            {{ exportMode === "image" ? "Copy Image" : "Print" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- hidden instance used as the editor for quotation→invoice conversion -->
    <add-invoice-card ref="convertInvoiceCard" />
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import AddQuotationDialog from "../components/Quotation/AddQuotationDialog.vue";
import ViewQuotationModal from "../components/Quotation/viewQuotationModal.vue";
import ConvertToInvoiceLauncher from "../components/Quotation/convertToInvoiceLauncher.vue";
import AddInvoiceCard from "../components/Invoice/addInvoiceCard.vue";
import { printQuotation, copyQuotationAsImage } from "../utils/quotationPrint";

export default {
  components: {
    AddQuotationDialog,
    ViewQuotationModal,
    ConvertToInvoiceLauncher,
    AddInvoiceCard,
  },
  data() {
    return {
      search: "",
      modeFilter: "all",
      statusFilter: "all",
      customerNameFilter: "",
      companyNameFilter: "",
      printDialog: false,
      printTarget: null,
      printOptions: {
        hideBundleItemPrices: false,
        hideAllItems: false,
      },
      exportMode: "pdf",
      copyingImage: false,
      pinLoading: null,
      duplicateLoading: null,
      headers: [
        { text: "Actions", value: "actions", sortable: false },
        { text: "ID", value: "id" },
        { text: "Notes", value: "notes" },
        { text: "Status", value: "is_draft", sortable: true },
        { text: "Mode", value: "quotation_mode" },
        { text: "Customer Name", value: "customer_name" },
        { text: "Company Name", value: "company_name" },
        { text: "Date", value: "quotation_date" },
        { text: "Total Quote Price", value: "total_quote_price" },
        { text: "Date Updated", value: "updated_at" },
      ],
    };
  },
  methods: {
    ...mapActions({
      get_quotations: "quotation/get_quotations",
      delete_quotation: "quotation/delete_quotation",
      toggle_pin_quotation: "quotation/toggle_pin_quotation",
      duplicate_quotation: "quotation/duplicate_quotation",
    }),
    async togglePin(item) {
      this.pinLoading = item.id;
      try {
        await this.toggle_pin_quotation(item);
      } catch (error) {
        this.$alert({
          title: "Error",
          message:
            error.response?.data?.message || "Failed to update pin status",
          color: "error",
          icon: "mdi-alert-circle",
        });
      } finally {
        this.pinLoading = null;
      }
    },
    onConvertQuote(payload) {
      this.$refs.convertInvoiceCard.openWithQuote(payload);
    },
    async duplicateQuotation(item) {
      const confirmed = await this.$confirm({
        title: "Duplicate Quotation",
        message: `Create a copy of Quote #${
          item.quote_number || item.id
        } as a new draft?`,
        color: "blue",
        icon: "mdi-content-copy",
      });
      if (!confirmed) return;

      this.duplicateLoading = item.id;
      try {
        await this.duplicate_quotation(item);
        this.$alert({
          title: "Success",
          message: "Quotation duplicated as a new draft",
          color: "success",
          icon: "mdi-check-circle",
        });
      } catch (error) {
        this.$alert({
          title: "Error",
          message:
            error.response?.data?.message || "Failed to duplicate quotation",
          color: "error",
          icon: "mdi-alert-circle",
        });
      } finally {
        this.duplicateLoading = null;
      }
    },
    openPrintDialog(item) {
      // Always fetch fresh data from store to ensure latest items after edits
      const fresh = this.$store.state.quotation.quotations.find(
        (q) => q.id === item.id
      );
      this.printTarget = fresh || item;
      this.printOptions.hideBundleItemPrices = false;
      this.printOptions.hideAllItems = false;
      this.exportMode = "pdf";
      this.printDialog = true;
    },
    async confirmPrint() {
      if (!this.printTarget) return;

      const opts = {
        hideBundleItemPrices: this.printOptions.hideBundleItemPrices,
        hideAllItems: this.printOptions.hideAllItems,
      };

      if (this.exportMode === "image") {
        this.copyingImage = true;
        try {
          const msg = await copyQuotationAsImage(this.printTarget, opts);
          this.$alert({
            title: "Success",
            message: msg,
            color: "success",
            icon: "mdi-check-circle",
          });
        } catch (err) {
          console.error("Image export error:", err);
          this.$alert({
            title: "Error",
            message:
              "Failed to copy image: " + (err.message || "Unknown error"),
            color: "error",
            icon: "mdi-alert-circle",
          });
        } finally {
          this.copyingImage = false;
        }
      } else {
        printQuotation(this.printTarget, opts);
      }

      this.printDialog = false;
    },
    async del(item) {
      let x = await this.$confirm({
        title: "Delete Quotation",
        message: "Are you sure you want to proceed?",
        color: "red",
        icon: "mdi-delete",
      });
      if (x) {
        this.delete_quotation(item).then(() => {
          this.$alert({
            title: "Success",
            message: "Successfully deleted",
            color: "success",
            icon: "mdi-check-circle",
          });
        });
      }
    },
    formatDate(val) {
      if (!val) return "";
      const d = new Date(val);
      return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    formatCurrency(val) {
      const num = parseFloat(val) || 0;
      return (
        "₱ " +
        num.toLocaleString("en-PH", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    },
  },
  computed: {
    ...mapGetters({
      quotations: "quotation/quotations",
    }),
    filteredQuotations() {
      let result = this.quotations;

      // Filter by quotation mode
      // Treat quotations with no quotation_mode (pre-migration data) as "walk_in"
      if (this.modeFilter && this.modeFilter !== "all") {
        result = result.filter(
          (q) => (q.quotation_mode || "walk_in") === this.modeFilter
        );
      }

      // Filter by draft status
      if (this.statusFilter === "draft") {
        result = result.filter((q) => q.is_draft);
      } else if (this.statusFilter === "finalized") {
        result = result.filter((q) => !q.is_draft);
      }

      // Filter by customer name
      if (this.customerNameFilter && this.customerNameFilter.trim() !== "") {
        const term = this.customerNameFilter.trim().toLowerCase();
        result = result.filter(
          (q) => q.customer_name && q.customer_name.toLowerCase().includes(term)
        );
      }

      // Filter by company name
      if (this.companyNameFilter && this.companyNameFilter.trim() !== "") {
        const term = this.companyNameFilter.trim().toLowerCase();
        result = result.filter(
          (q) => q.company_name && q.company_name.toLowerCase().includes(term)
        );
      }

      // Sort pinned items to the top, then by id descending
      result.sort((a, b) => {
        if (a.is_pinned && !b.is_pinned) return -1;
        if (!a.is_pinned && b.is_pinned) return 1;
        return b.id - a.id;
      });

      // Always return a new array so the data table detects changes after edits
      return [...result];
    },
  },
  created() {
    if (this.quotations.length == 0) this.get_quotations();
  },
};
</script>

<style></style>
